import { findProductByPartNo } from "@automatry/catalog";
import type { CatalogProduct, CatalogRelease, SourceRef } from "@automatry/schemas";

import currentEngineFixtureUrl from "../fixtures/carrington-engine-fixture.json?url";
import type {
  ProjectFixtureQuoteCatalogProduct,
  ProjectFixtureQuotePricing
} from "../viewmodel/projectCockpitAdapter";
import type { ProjectFixtureWithLaneB } from "./projectData";

type CatalogReleaseWithProducts = CatalogRelease & {
  products: CatalogProduct[];
};

const PRICE_LIST_NAME = "Titan Products 2026 Gold";
const PRICE_LIST_VERSION = "2026-01-gold";
const PRICE_LIST_DATE = "2026-01";
const MAX_CONTEXT_SOURCE_REFS = 8;

let currentCatalogReleasePromise: Promise<CatalogReleaseWithProducts> | null = null;

const locatorForDisplay = (locator: string): string =>
  locator.replace(/#span-\d+$/, "").replace(/#takeoff$/, "");

const sourceRefForDisplay = (sourceRef: SourceRef): SourceRef => ({
  sourceId: sourceRef.sourceId,
  sourceKind: sourceRef.sourceKind,
  authorityClass: sourceRef.authorityClass,
  locator: locatorForDisplay(sourceRef.locator),
  ...(sourceRef.page !== undefined ? { page: sourceRef.page } : {}),
  ...(sourceRef.sheetName ? { sheetName: sourceRef.sheetName } : {}),
  ...(sourceRef.cellRef ? { cellRef: sourceRef.cellRef } : {}),
  ...(sourceRef.rangeRef ? { rangeRef: sourceRef.rangeRef } : {})
});

const sourceRefDisplayKey = (sourceRef: SourceRef): string =>
  [
    sourceRef.sourceKind,
    sourceRef.authorityClass,
    locatorForDisplay(sourceRef.locator)
  ].join("|");

const sourceRefsForDisplay = (sourceRefs: readonly SourceRef[] = []): SourceRef[] => {
  const refsByKey = new Map<string, SourceRef>();

  for (const sourceRef of sourceRefs) {
    const key = sourceRefDisplayKey(sourceRef);

    if (!refsByKey.has(key)) {
      refsByKey.set(key, sourceRefForDisplay(sourceRef));
    }
  }

  return [...refsByKey.values()];
};

const metadataString = (
  metadata: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = metadata?.[key];

  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const compactQuoteCatalogProduct = (
  product: CatalogProduct
): ProjectFixtureQuoteCatalogProduct => {
  const commercialModel = metadataString(product.metadata, "commercialModel");
  const edgeRole = metadataString(product.metadata, "edgeRole");
  const capabilities = {
    ...(product.capabilities.primaryApplication
      ? { primaryApplication: product.capabilities.primaryApplication }
      : {}),
    ...(product.capabilities.roles.length > 0
      ? { roles: product.capabilities.roles }
      : {}),
    ...(product.capabilities.notes.length > 0
      ? { notes: product.capabilities.notes }
      : {})
  };
  const metadata = {
    ...(commercialModel ? { commercialModel } : {}),
    ...(edgeRole ? { edgeRole } : {})
  };

  return {
    partNo: product.partNo,
    aliases: product.aliases,
    description: product.description,
    ...(product.family ? { family: product.family } : {}),
    sourceRefs: sourceRefsForDisplay(product.sourceRefs),
    ...(Object.keys(capabilities).length > 0 ? { capabilities } : {}),
    ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    ...(product.price
      ? {
          price: {
            amount: product.price.amount,
            currency: product.price.currency,
            sourceRefs: sourceRefsForDisplay(product.price.sourceRefs)
          }
        }
      : {})
  };
};

const pricingContextSourceRefs = (
  catalog: CatalogReleaseWithProducts,
  products: readonly CatalogProduct[]
): SourceRef[] => {
  const priceRefs = sourceRefsForDisplay(
    products.flatMap((product) => product.price?.sourceRefs ?? [])
  );
  const releaseRefs = sourceRefsForDisplay(catalog.sourceRefs);
  const refs = priceRefs.length > 0 ? priceRefs : releaseRefs;

  if (refs.length === 0) {
    throw new Error("Quote pricing context needs at least one catalog source ref.");
  }

  return refs.slice(0, MAX_CONTEXT_SOURCE_REFS);
};

export const buildProjectQuotePricingFromCatalogRelease = (
  fixture: ProjectFixtureWithLaneB,
  catalog: CatalogReleaseWithProducts
): ProjectFixtureQuotePricing | null => {
  if (fixture.project.activeCatalogReleaseId !== catalog.id) {
    return null;
  }

  const productsByPartNo = new Map<string, CatalogProduct>();

  for (const partNo of new Set(
    fixture.outputBundle.bomRows
      .map((row) => row.productPartNo)
      .filter((partNo): partNo is string => Boolean(partNo))
  )) {
    const product = findProductByPartNo(catalog.products, partNo);

    if (product) {
      productsByPartNo.set(product.partNo, product);
    }
  }

  const products = [...productsByPartNo.values()].sort((left, right) =>
    left.partNo.localeCompare(right.partNo)
  );

  return {
    generatedAt: fixture.fixture.generatedAt,
    pricingContext: {
      currency: "GBP",
      catalogReleaseId: catalog.id,
      catalogVersion: catalog.version,
      priceListName: PRICE_LIST_NAME,
      priceListVersion: PRICE_LIST_VERSION,
      priceListDate: PRICE_LIST_DATE,
      sourceRefs: pricingContextSourceRefs(catalog, products)
    },
    catalogProducts: products.map(compactQuoteCatalogProduct)
  };
};

export const loadCurrentQuoteCatalogRelease = async (): Promise<CatalogReleaseWithProducts> => {
  currentCatalogReleasePromise ??= (async () => {
    const response = await fetch(currentEngineFixtureUrl, { cache: "no-store" });

    if (!response.ok) {
      throw new Error("catalog release request failed: " + response.status);
    }

    const fixture = (await response.json()) as { catalog: CatalogReleaseWithProducts };

    return fixture.catalog;
  })();

  return currentCatalogReleasePromise;
};

export const ensureGenericQuotePricing = async (
  fixture: ProjectFixtureWithLaneB
): Promise<ProjectFixtureWithLaneB> => {
  if (fixture.quotePricing) {
    return fixture;
  }

  const catalog = await loadCurrentQuoteCatalogRelease();
  const quotePricing = buildProjectQuotePricingFromCatalogRelease(fixture, catalog);

  return quotePricing ? { ...fixture, quotePricing } : fixture;
};
